import { throttle } from "../utils/performance";
export default function(el) {
    const chaptersNav = el;
    const mainNav = document.querySelector('.nav');
    const chapterLinks = document.querySelectorAll('.subNav-chapters__link');
    const sections = document.querySelectorAll('section');
    const brick = document.getElementById("brick");
    const chapterHeight = 48; // Height of chapters - 48px

    let lastActiveSection = null;
    let isManualScroll = false; // Flag to track if scroll was triggered by click

    // Track chapter IDs and last boundary bottom for hide/slide-up
    const chapterIds = Array.from(chapterLinks).map(link => link.getAttribute('href')?.slice(1)).filter(Boolean);
    let lastBoundaryBottom = 0;

    /** 🟢 Assigns `data-id` to sections based on `data-range` */
    function assignDataIds() {
        sections.forEach((section, index) => {
            if (section.id) {
                const range = parseInt(section.getAttribute('data-range')) || 0;
                section.setAttribute('data-id', section.id); // Set its own ID

                // Apply the same data-id to the next N sections, but stop if a section has an ID
                for (let i = 1; i <= range; i++) {
                    const nextSection = sections[index + i];
                    if (!nextSection || nextSection.id) break; // Stop if we hit another ID
                    nextSection.setAttribute('data-id', section.id);
                }
            }
        });
    }

    function getNavHeights() {
        const mainNavHeight = mainNav?.offsetHeight || 0;

        // Check for signature navigation
        const signatureNav = document.querySelector('.sg-w');
        const signatureNavHeight = signatureNav?.offsetHeight || 0;

        // Check for ads bar
        const adsBar = document.querySelector('.ads-w--bar');
        const adsBarHeight = adsBar?.offsetHeight || 0;

        // Sum all navigation heights
        return mainNavHeight + signatureNavHeight - adsBarHeight;
    }

    // Compute the bottom boundary (end) of the last tracked chapter section
    function computeLastBoundary() {
        const lastId = chapterIds[chapterIds.length - 1];
        if (!lastId) {
            lastBoundaryBottom = 0;
            return;
        }

        // Prefer the last section that carries data-id equal to lastId (to honor data-range)
        const tracked = Array.from(sections).filter(sec => sec.getAttribute('data-id') === lastId);
        let lastSection = tracked[tracked.length - 1];

        // Fallback to the actual section by ID
        if (!lastSection) {
            lastSection = document.getElementById(lastId);
        }

        if (lastSection) {
            lastBoundaryBottom = lastSection.offsetTop + lastSection.offsetHeight;
        } else {
            lastBoundaryBottom = 0;
        }
    }

    function updateActiveClass() {
        // Skip if manual scroll is in progress
        if (isManualScroll) return;

        const navHeights = getNavHeights();
        const scrollPosition = window.scrollY + navHeights + (chapterHeight + 10);
        let newActiveSection = null;

        sections.forEach((section) => {
            const sectionId = section.getAttribute('data-id'); // Use `data-id` instead of real `id`
            if (!sectionId) return; // Ignore sections without `data-id`

            const sectionTop = section.offsetTop;
            const sectionBottom = sectionTop + section.offsetHeight;
            if (scrollPosition >= sectionTop && scrollPosition < sectionBottom) {
                newActiveSection = sectionId;
            }
        });

        // ✅ If scrolling over a section without `data-id`, remove highlight
        if (!newActiveSection) {
            updateNavHighlight(null);
            lastActiveSection = null;
            return;
        }

        // ✅ Only update highlight if there's a change
        if (newActiveSection !== lastActiveSection) {
            lastActiveSection = newActiveSection;
            updateNavHighlight(newActiveSection);
        }
    }

    function updateNavHighlight(activeSection) {
        chapterLinks.forEach((link) => {
            const parentLi = link.closest('li');
            if (activeSection && link.getAttribute('href').slice(1) === activeSection) {
                parentLi?.classList.add('active');
            } else {
                parentLi?.classList.remove('active');
            }
        });
    }

    function checkBrickPosition() {
        const totalNavHeight = getNavHeights();
        const subNav = document.querySelector('.subNav-chapters');

        if (!mainNav || !subNav || !brick) return;

        const brickSection = brick.closest('section');
        const nextSection = brickSection?.nextElementSibling;

        const subNavOriginalTop = brick.offsetTop;
        const scrollPosition = window.scrollY;
        const nextSectionTop = nextSection?.offsetTop || 0;

        if (scrollPosition + totalNavHeight < subNavOriginalTop) {
            subNav.style.position = 'absolute';
            subNav.style.top = `${nextSectionTop - chapterHeight}px`; // Deduct chapter height
        } else {
            subNav.style.position = 'fixed';
            subNav.style.top = `${totalNavHeight}px`;
        }
    }

    // Hide/slide-up the subNav after passing the last tracked section
    function updateSubNavVisibility() {
        const subNav = document.querySelector('.subNav-chapters');
        if (!subNav) return;

        if (!lastBoundaryBottom) computeLastBoundary();

        const navHeights = getNavHeights();
        const scrollPosition = window.scrollY + navHeights; // top edge of fixed subnav
        const shouldHide = lastBoundaryBottom > 0 && (scrollPosition + chapterHeight >= lastBoundaryBottom);

        // Apply a lightweight slide-up effect via transform
        subNav.style.transition = 'transform 200ms ease';
        subNav.style.transform = shouldHide ? 'translateY(-100%)' : 'translateY(0)';
    }

    // Handle scroll end detection
    function onScrollEnd() {
        isManualScroll = false;
        // Final update after scroll completes
        updateActiveClass();
        updateSubNavVisibility();
    }

    // Create a debounced scroll end handler
    const debouncedScrollEnd = (() => {
        let timer;
        return function() {
            if (timer) clearTimeout(timer);
            timer = setTimeout(onScrollEnd, 100); // 100ms after scroll stops
        };
    })();

    // 🟢 Smooth scrolling when clicking nav links
    chapterLinks.forEach((link) => {
        link.addEventListener('click', (event) => {
            event.preventDefault();

            const targetId = link.getAttribute('href').slice(1);
            const targetSection = document.getElementById(targetId);

            if (targetSection) {
                // First update highlight and set active section
                lastActiveSection = targetId;
                updateNavHighlight(targetId);

                // Set manual scroll flag to prevent scroll events from changing highlight
                isManualScroll = true;

                // Calculate scroll position
                const targetPosition = targetSection.offsetTop - getNavHeights() - chapterHeight;

                // Perform scroll
                window.scrollTo({
                    top: targetPosition,
                    behavior: 'smooth',
                });

                // Listen for scroll end to reset the flag
                window.addEventListener('scroll', debouncedScrollEnd, { passive: true });
            }
        });
    });

    assignDataIds();
    computeLastBoundary();

    // Initialize the scroll event listeners
    window.addEventListener('scroll', throttle(checkBrickPosition, 100));
    window.addEventListener('scroll', throttle(updateActiveClass, 100));
    window.addEventListener('scroll', throttle(updateSubNavVisibility, 100));

    // Use multiple loading events to ensure proper initial positioning

    // Run on DOMContentLoaded (when HTML is fully parsed)
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
            checkBrickPosition();
            updateActiveClass();
            updateSubNavVisibility();
        });
    } else {
        // Document already loaded
        checkBrickPosition();
        updateActiveClass();
        updateSubNavVisibility();
    }

    // Run on load (when all resources like images are loaded)
    window.addEventListener('load', () => {
        checkBrickPosition();
        updateActiveClass();
        updateSubNavVisibility();
    });

    // Add a delayed check to catch any late layout shifts
    setTimeout(() => {
        checkBrickPosition();
        updateActiveClass();
        updateSubNavVisibility();
    }, 200);

    // Use ResizeObserver if available to detect layout changes
    if (typeof ResizeObserver === 'function') {
        const resizeObserver = new ResizeObserver(throttle(() => {
            computeLastBoundary();
            checkBrickPosition();
            updateActiveClass();
            updateSubNavVisibility();
        }, 100));

        // Observe the brick element and the main content area
        if (brick) resizeObserver.observe(brick);
        const mainContent = document.querySelector('main') || document.body;
        resizeObserver.observe(mainContent);
    }
}